import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { MessageSquareQuote, Plus, X, Check, Clock } from "lucide-react";
import ReviewSubmitModal from "@/components/ReviewSubmitModal";

/**
 * User-facing testimonial manager — lives inside ProfilePanel. Lets a
 * satisfied user submit a short quote that (once admin-approved) joins
 * the rotation pool used by the affiliate posters.
 *
 * The list on this panel shows every quote the user has submitted plus
 * its live status (pending / approved / rejected) so people can track
 * what happened to their submission.
 */
export default function TestimonialsSection() {
  const { t } = useT();
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/testimonials/mine");
      setMine(Array.isArray(data) ? data : []);
    } catch {
      setMine([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const withdraw = async (id) => {
    try {
      await api.delete(`/testimonials/mine/${id}`);
      toast.success(t("tsWithdrawnDone"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tsWithdrawFailed"));
    }
  };

  const statusMeta = {
    pending: { Icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-200", key: "tsStatusPending" },
    approved: { Icon: Check, cls: "text-emerald-800 bg-emerald-50 border-emerald-200", key: "tsStatusApproved" },
    rejected: { Icon: X, cls: "text-rose-800 bg-rose-50 border-rose-200", key: "tsStatusRejected" },
  };

  return (
    <div className="ed-card p-6 space-y-5" data-testid="testimonials-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <MessageSquareQuote size={16} strokeWidth={1.5} className="mt-0.5 shrink-0 text-neutral-500" />
          <div className="min-w-0">
            <p className="ed-eyebrow">{t("tsEyebrow")}</p>
            <h3 className="font-serif text-2xl leading-tight mt-1">{t("tsTitle")}</h3>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed max-w-xl">
              {t("tsBody")}
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="testimonial-open-submit"
          onClick={() => setOpenModal(true)}
          className="ed-btn ed-btn-primary inline-flex items-center gap-2 shrink-0"
        >
          <Plus size={13} strokeWidth={1.75} /> {t("tsSubmitCta")}
        </button>
      </div>

      {loading ? (
        <p className="text-xs font-mono text-neutral-500 uppercase tracking-[0.15em]">
          {t("tsLoading")}
        </p>
      ) : mine.length === 0 ? (
        <div className="border border-dashed border-neutral-300 bg-neutral-50 p-5 text-center">
          <p className="text-sm text-neutral-600" data-testid="testimonials-empty">
            {t("tsEmpty")}
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="testimonials-list">
          {mine.map((it) => {
            const meta = statusMeta[it.status] || statusMeta.pending;
            const StatusIcon = meta.Icon;
            return (
              <li key={it.id} className="border border-neutral-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className="font-serif italic text-base leading-snug min-w-0 flex-1">
                    “{it.body}”
                  </p>
                  <span
                    className={`inline-flex items-center gap-1 border px-2 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] shrink-0 ${meta.cls}`}
                    data-testid={`testimonial-status-${it.status}`}
                  >
                    <StatusIcon size={11} strokeWidth={1.75} />
                    {t(meta.key)}
                  </span>
                </div>
                <p className="mt-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
                  {[it.alias, it.role, it.city].filter(Boolean).join(" · ") || t("tsAnonymous")}
                  {" · "}
                  {(it.lang || "nl").toUpperCase()}
                </p>
                {it.status === "pending" && (
                  <button
                    type="button"
                    data-testid={`testimonial-withdraw-${it.id}`}
                    onClick={() => withdraw(it.id)}
                    className="mt-3 inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900"
                  >
                    <X size={11} strokeWidth={1.75} /> {t("tsWithdraw")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ReviewSubmitModal
        open={openModal}
        onClose={() => { setOpenModal(false); refresh(); }}
      />
    </div>
  );
}
